'use client'

import * as React from 'react'
import { useRouter, usePathname } from '@repo/shared/i18n/navigation'
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@repo/ui/components/tabs'

export interface RoutedTab {
  value: string
  label: string
  href: string
  content?: React.ReactNode
}

interface RoutedTabsProps {
  tabs: RoutedTab[]
  defaultTab?: string
  size?: 'sm' | 'md' | 'lg'
  color?: 'neutral' | 'base'
  scrollable?: boolean
  className?: string
}

export function RoutedTabs({
  tabs,
  defaultTab,
  size = 'md',
  color = 'neutral',
  scrollable = false,
  className,
}: RoutedTabsProps) {
  const router = useRouter()
  const pathname = usePathname()
  const [activeTab, setActiveTab] = React.useState<string>(
    defaultTab || tabs[0]?.value || '',
  )
  const prefetchedRef = React.useRef<Set<string>>(new Set())

  // Update active tab when pathname changes
  React.useEffect(() => {
    const currentTab = tabs.find((tab) => tab.href + '/' === pathname)
    if (currentTab) {
      setActiveTab(currentTab.value)
    }
  }, [pathname, tabs])

  // Prefetch all tab routes on mount for better performance
  React.useEffect(() => {
    tabs.forEach((tab) => {
      if (!prefetchedRef.current.has(tab.href)) {
        router.prefetch(tab.href)

        prefetchedRef.current.add(tab.href)
      }
    })
  }, [tabs])

  const handleTabChange = (value: string) => {
    const tab = tabs.find((t) => t.value === value)
    if (tab) {
      setActiveTab(value)
      router.push(tab.href)
    }
  }

  return (
    <Tabs
      value={activeTab}
      onValueChange={handleTabChange}
      size={size}
      color={color}
      scrollable={scrollable}
      className={className}
    >
      <TabsList className='w-full md:w-fit'>
        {tabs.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value}>
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  )
}
